import type { Alert } from '../hooks/useAlerts';

/**
 * The Hub's notification ROW MODEL — the one place that decides which
 * notification rows a Hub account surface shows, in what order, and therefore
 * what the count is.
 *
 * WHY THIS IS A NEW MODULE AND NOT AN EDIT TO `AccountAlerts.tsx`. That
 * component is the shared renderer for `V4AccountDropdown`, the ten non-Hub
 * mount points of `HeaderAccountMenu`, and `BoardAlertsBell` — every one of
 * them a page the PRD marks `NO CHANGE` ("every page with the Hub switch off").
 * Removing its roll-up in place would change all of them. So the Hub gets its
 * own model, and `AccountAlerts` keeps today's behaviour untouched.
 *
 * THREE RULES, and the count falls out of them rather than being computed
 * separately (THE-973 / THE-967 PR 3):
 *
 *  1. NO ROLL-UP. Every `add_birthday` row renders on its own. `AccountAlerts`
 *     collapses three or more into "Add birthdays for Fred, Esme + 6 more" with
 *     an expand chevron; the PRD removes that for the Hub, because a collapsed
 *     row made the avatar number and the visible row count disagree.
 *  2. TIME-SENSITIVE FIRST. `coming_up_soon` ahead of everything else, same as
 *     `AccountAlerts`. `isPast` rows never render — `useAlerts` already drops
 *     them, and this filter is the same defensive second pass `AccountAlerts`
 *     keeps (the BE retains a day-of alert an extra day so every timezone gets
 *     its full local day; the FE is the layer that knows the viewer's calendar
 *     has moved on).
 *  3. THE UNCONFIRMED-EMAIL STAGE IS A GATE, NOT A BANNER. While the account's
 *     address is unconfirmed, the list is a `confirm-email` row plus birthday
 *     and anniversary reminders ONLY — every other notification waits until the
 *     address is confirmed. The chores are the ones that wait: asking her to
 *     fill in a birthday is noise next to "we cannot reach you yet".
 *
 * `hubAlertCount(rows) === rows.length` BY CONSTRUCTION. That identity is the
 * reason the model returns rows rather than a count plus a list: the desktop
 * avatar's number (PR 1), the mobile tab-bar dot (PR 3) and the rendered list
 * all read this one array, so they cannot drift. A dismissal drops a row, which
 * drops the count, which clears the dot — one mechanism, three surfaces.
 */

export type HubAlertRow =
  /** "Confirm your email". Carries no ✕ — it is a gate, not a notification she
   *  can clear. Its inline confirm box is the host's to render. */
  | { kind: 'confirm-email'; id: 'confirm-email' }
  | { kind: 'alert'; id: string; alert: Alert };

export interface HubAlertRowsInput {
  alerts: Alert[];
  /**
   * The signed-in account has an email address Firebase has not confirmed yet.
   * FALSE for guests and for confirmed accounts alike — a guest has no address
   * to confirm, so she must never see the gate row.
   */
  needsEmailConfirm: boolean;
}

/** True for the reminders that survive the unconfirmed-email gate. */
const isReminder = (a: Alert): boolean => a.type === 'coming_up_soon';

export function hubAlertRows({ alerts, needsEmailConfirm }: HubAlertRowsInput): HubAlertRow[] {
  const live = alerts.filter((a) => !a.isPast);
  const reminders = live.filter(isReminder);
  // Rule 3: the gate admits reminders and nothing else. Rule 2 otherwise:
  // reminders first, then everything else in `useAlerts`' incoming order
  // (newest-first by `createdAt`).
  const body = needsEmailConfirm ? reminders : [...reminders, ...live.filter((a) => !isReminder(a))];
  const rows: HubAlertRow[] = body.map((alert) => ({ kind: 'alert', id: alert.alertId, alert }));
  return needsEmailConfirm
    ? [{ kind: 'confirm-email', id: 'confirm-email' }, ...rows]
    : rows;
}

/**
 * The count every Hub surface shows. Identical to `rows.length` — exported as a
 * named function anyway so the desktop avatar number, the tab-bar dot and the
 * list cannot acquire three different ideas of what "a notification" is.
 */
export function hubAlertCount(rows: readonly HubAlertRow[]): number {
  return rows.length;
}
